import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { PasswordRecovery } from './features/auth/PasswordRecovery';
import { createLuviaCore } from './legacy/core';
import { EnvConfigError, getEnv, readAuthRedirect, reportClientError } from './lib/supabase';
import type { Json } from './lib/supabase/database.types';


/**
 * Reports uncaught errors into public.client_errors (the no-third-party-account APM
 * alternative — 20261008030000_client_error_log.sql). Capped per page load so a render
 * loop that throws repeatedly can't flood the table; silently no-ops until LuviaCore has
 * resolved a workspace (startup/config errors are already shown to the user via showFatal).
 */
(function registerGlobalErrorHandler() {
  const MAX_REPORTS_PER_LOAD = 20;
  let reported = 0;

  function safeContext(extra: Record<string, unknown>): Json | null {
    try {
      return JSON.parse(
        JSON.stringify(extra, (_key, value) =>
          value instanceof Error ? { name: value.name, message: value.message } : value,
        ),
      ) as Json;
    } catch {
      return null;
    }
  }

  function report(
    kind: 'error' | 'unhandledrejection',
    message: string,
    stack: string | null | undefined,
    extra: Record<string, unknown>,
  ) {
    try { console.error('[global-error:' + kind + ']', extra); } catch (e) { /* console unavailable */ }
    if (reported >= MAX_REPORTS_PER_LOAD) return;
    const info = window.LuviaCore?.info();
    const workspaceId = info?.workspace?.id;
    if (!info || !workspaceId) return; // no resolved workspace yet — nothing to attribute the row to
    reported += 1;
    void reportClientError(workspaceId, info.appKey, info.email, {
      kind,
      message,
      stack: stack ?? null,
      context: safeContext(extra),
    });
  }

  window.addEventListener('error', (e) => {
    report('error', e.message, e.error && (e.error as Error).stack, { source: e.filename, line: e.lineno, col: e.colno });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason as { stack?: string; message?: string } | undefined;
    report('unhandledrejection', (reason && (reason.message || String(reason))) || 'unhandled rejection', reason?.stack, { reason });
  });
})();

/**
 * Boot order
 *   1. Read the auth redirect fragment (password reset links) before supabase-js consumes it.
 *   2. createLuviaCore(): restore the Supabase session and load settings, members and
 *      records for the workspace from Postgres.  ← EMPLOYEE-PC SYNC FIX: this runs on every PC
 *   3. Load the legacy UI scripts, which now read everything through window.LuviaCore.
 */

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

function showFatal(title: string, detail: string): void {
  document.documentElement.classList.remove('is-auth');
  const view = document.getElementById('loginView');
  const wrap = view?.querySelector('.wrap');
  if (!view || !wrap) {
    document.body.textContent = `${title}: ${detail}`;
    return;
  }
  view.style.display = '';
  const card = document.createElement('div');
  card.className = 'login-card';
  const heading = document.createElement('h1');
  heading.textContent = 'LUVIA TOUR';
  const tag = document.createElement('div');
  tag.className = 'tag';
  tag.textContent = title;
  const body = document.createElement('div');
  body.className = 'msg';
  body.style.whiteSpace = 'pre-wrap';
  body.textContent = detail;
  card.append(heading, tag, body);
  wrap.replaceChildren(card);
}

function mountRecovery(linkError: string | null): void {
  document.documentElement.classList.remove('is-auth');
  const view = document.getElementById('loginView');
  const wrap = view?.querySelector('.wrap');
  if (!view || !wrap) return;
  view.style.display = '';
  const host = document.createElement('div');
  wrap.replaceChildren(host);
  createRoot(host).render(createElement(PasswordRecovery, { linkError }));
}

async function main(): Promise<void> {
  const redirect = readAuthRedirect(window.location.hash);

  try {
    getEnv();
  } catch (err: unknown) {
    showFatal('Configuration error', err instanceof EnvConfigError ? err.message : String(err));
    return;
  }

  // Password-reset link: only the recovery screen is needed, not the dashboard data.
  if (redirect.isRecovery || redirect.errorDescription) {
    mountRecovery(redirect.errorDescription);
    return;
  }

  try {
    window.LuviaCore = await createLuviaCore();
  } catch (err: unknown) {
    showFatal('Startup error', err instanceof Error ? err.message : String(err));
    return;
  }

  try {
    // Both <script> tags are appended synchronously here (and have async=false), so the browser
    // fetches them in parallel while still executing i18next before app.js once both arrive —
    // app.js calls i18next.init() at load time and needs the global to already exist.
    await Promise.all([loadScript('/legacy/vendor/i18next.min.js'), loadScript('/legacy/i18n-extra.js'), loadScript('/legacy/app.js')]);
  } catch (err: unknown) {
    showFatal('Startup error', err instanceof Error ? err.message : String(err));
  }
}

void main();
